import styled, { createGlobalStyle } from 'styled-components';
import { GlobalToken } from 'antd';

import { Select } from '../Common/styled';

export const CompressorsGroupContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  overflow: scroll;
  margin-right: -${({ theme: { token } }) => token.marginSM}px;

  .compressor-groups {
    display: grid;
    grid-template-columns: minmax(0, 50%) minmax(0, 50%);
    gap: 40px;

    .compressor-group {
      display: flex;
      flex-direction: column;
    }

    .disabled {
      pointer-events: none;
    }

    .compressor-group-title {
      display: flex;
      background-color: ${({ theme: { token } }) => token.colorBgElevated};
      padding: ${({ theme: { token } }) =>
        `${token.paddingXS}px ${token.paddingSM}px ${token.paddingXS}px ${token.padding}px`};
      margin-bottom: ${({ theme: { token } }) => token.marginXS}px;

      .compressor-group-name {
        color: ${({ theme: { token } }) => token.colorPrimary};
        background-color: ${({ theme: { token } }) => token.colorBgElevated};
        border: none;
        flex-grow: 1;
      }

      .rotation-options {
        display: flex;
        align-items: center;
        color: ${({ theme: { token } }) => token.colorTextSecondary};

        .rotation-value {
          text-decoration: underline;
          cursor: pointer;
          margin-right: ${({ theme: { token } }) => token.marginXS}px;
        }

        .delete-item-icon {
          margin-bottom: ${({ theme: { token } }) => token.marginXXS}px;
        }
      }
    }
  }

  .add-compressor-group-button {
    align-self: center;
    margin-top: ${({ theme: { token } }) => token.marginMD}px;
  }
`;

export const RotationInfoPopoverContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme: { token } }) => token.colorBgContainer};
  border: solid 0.5px ${({ theme: { token } }) => token.colorBorder};
  padding: ${({ theme: { token } }) => `${token.paddingMD}px`};

  .compressors-group-info {
    display: flex;
    align-items: center;

    &:not(:last-child) {
      margin-bottom: ${({ theme: { token } }) => token.margin}px;
    }

    .compressors-group-info-label {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorBorder};
      width: 84px;
    }
  }

  .disabled {
    opacity: 0.5;
  }
`;

export const PopoverGlobalStyle = createGlobalStyle`
  /* hide number input arrows - Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* hide number input arrows - Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  .ant-popover.rotation {
    .ant-popover-inner {
      padding: 0;
    }

    .ant-popover-arrow {
      display: none;
    }
  }
`;

interface RepeatOnSwitchContainerProps {
  $disabled: boolean;
  theme: {
    token: GlobalToken;
  };
}

export const RepeatOnSwitchContainer = styled.div<RepeatOnSwitchContainerProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 183px;
  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.5;
  `}

  .day {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
    width: 21px;
    height: 21px;
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    border: solid 0.5px ${({ theme: { token } }) => token.colorBorder};
    border-radius: 20px;
    background-color: ${({ theme: { token } }) => token.colorBgElevated};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    color: ${({ theme: { token } }) => token.colorText};
  }

  .selected {
    background-color: ${({ theme: { token } }) => token.colorTextSecondary};
    color: ${({ theme: { token } }) => token.colorBgBase};
  }
`;

export const ListContainer = styled.div<{ theme: { token: GlobalToken } }>`
  margin-top: ${({ theme: { token } }) => token.marginXS}px;
  border: none;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  .list-item {
    display: flex;
    align-items: center;
    padding: 0 ${({ theme: { token } }) => token.paddingXXS}px;
    background-color: ${({ theme: { token } }) => token.colorBgElevated};
    border-radius: 6px;

    .list-item-title {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      color: ${({ theme: { token } }) => token.colorTextTertiary};
      margin: ${({ theme: { token } }) => token.marginXXS}px 0 0 ${({ theme: { token } }) => token.marginXS}px;
      cursor: default;
      white-space: nowrap;
    }
  }

  .draggable-icon-stacked {
    cursor: pointer;
  }

  [draggable='true'] {
    background-color: ${({ theme: { token } }) => token.colorBgElevated};
  }

  .icon-close {
    cursor: pointer;
  }

  .validation-error-alert {
    margin-bottom: 8px;
    color: ${({ theme: { token } }) => token.colorError};
  }
`;

export const DragLineGlobalStyle = createGlobalStyle`
  .drag-line {
    border-color: transparent !important;
  }
`;

export const SelectCompressor = styled(Select)<{
  theme: { token: GlobalToken };
}>`
  &.ant-select {
    min-width: 250px;
    height: 20px;

    .ant-select-selector {
      border: none;
      padding: 0;
      height: 20px;

      .ant-select-selection-overflow {
        display: none;
      }

      .ant-select-selection-item {
        background-color: ${({ theme: { token } }) => token.colorBgContainer};
      }
    }

    .ant-select-arrow {
      left: 0;
      position: absolute;

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  &.ant-select + div {
    width: fit-content;
  }
`;

export const SelectDropdownStyle = createGlobalStyle`
  .dropdown-style.ant-select-dropdown {
    min-width: fit-content !important;
  }
`;
